/**
 * 认证 API
 */

import type { BackendResponse } from './types/common';
import { request } from './request';
import type {
  CheckPhoneRegistrationParams,
  CheckPhoneRegistrationResponse,
  CheckUsernameRegistrationParams,
  CheckUsernameRegistrationResponse,
  ChangeUserPasswordParams,
  LoginParams,
  RegisterParams,
  RegisterResponse,
  SendVerificationCodeParams,
  SendVerificationCodeResponse,
  ResetPasswordParams,
  TokenResponse,
  UserLoginParams,
  UserInfoResponse,
  VerifyResetPasswordCodeParams,
  VerifyResetPasswordCodeResponse,
  VerifySmsCodeParams,
  VerifySmsCodeResponse,
} from './types/auth';

/**
 * 认证 API 类
 */
export class AuthAPI {

  /**
   * 发送短信验证码
   * @param params 手机号和区号（camelCase，自动转换）
   * @returns 发送结果
   */
  static async sendVerificationCode(
    params: SendVerificationCodeParams
  ): Promise<BackendResponse<SendVerificationCodeResponse>> {
    return request.post<SendVerificationCodeResponse>('/auth/send-verification-code', {
      ...params,
    });
  }

  /**
   * 用户注册
   * @param params 注册信息（camelCase，自动转换）
   * @returns Token 信息（已转换为 camelCase）
   */
  static async register(params: RegisterParams): Promise<BackendResponse<RegisterResponse>> {
    return request.post<RegisterResponse>('/auth/register', {
      ...params,
    });
  }

  /**
   * 检查手机号是否已注册
   * @param params 手机号
   * @returns 注册状态
   */
  static async checkPhoneRegistration(
    params: CheckPhoneRegistrationParams
  ): Promise<BackendResponse<CheckPhoneRegistrationResponse>> {
    return request.get<CheckPhoneRegistrationResponse>('/auth/check-phone', {
      params,
    });
  }

  /**
   * 检查用户名是否已注册
   * @param params 用户名
   * @returns 注册状态
   */
  static async checkUsernameRegistration(
    params: CheckUsernameRegistrationParams
  ): Promise<BackendResponse<CheckUsernameRegistrationResponse>> {
    return request.get<CheckUsernameRegistrationResponse>('/auth/check-username', {
      params,
    });
  }

  /**
   * 用户登录（旧版，不使用）
   * @param params 登录信息（camelCase，自动转换）
   * @returns Token 信息（已转换为 camelCase）
   */
  static async login(params: LoginParams): Promise<BackendResponse<TokenResponse>> {
    return request.post<TokenResponse>('/auth/login', params);
  }

  /**
   * 用户登录（新版，使用用户名密码 + 图形验证码）
   * @param params 登录信息（camelCase，自动转换）
   * @returns Token 信息（已转换为 camelCase）
   */
  static async userLogin(params: UserLoginParams): Promise<BackendResponse<TokenResponse>> {
    return request.post<TokenResponse>('/user/login', params);
  }

  /**
   * 获取登录图形验证码
   * @returns 验证码图片（Base64 字符串）
   */
  static async getLoginValidateImage(): Promise<BackendResponse<string>> {
    return request.get<string>('/user/get-login-validate-image');
  }

  /**
   * 获取当前用户信息
   * @returns 用户信息（已转换为 camelCase）
   */
  static async getUserInfo(): Promise<BackendResponse<UserInfoResponse>> {
    return request.get<UserInfoResponse>('/user/info');
  }

  /**
   * 用户退出登录
   * @returns 退出结果
   */
  static async logout(): Promise<BackendResponse<string>> {
    return request.get<string>('/user/logout');
  }

  /**
   * 修改当前用户密码
   * @param params 修改密码信息
   * @returns 修改结果
   */
  static async changeUserPassword(params: ChangeUserPasswordParams): Promise<BackendResponse<null>> {
    return request.post<null>('/user/change-user-password', params);
  }

  /**
   * 重置密码
   * @param params 重置密码信息（camelCase，自动转换）
   * @returns 重置结果
   */
  static async resetPassword(params: ResetPasswordParams): Promise<BackendResponse<null>> {
    return request.post<null>('/auth/password/reset', params);
  }

  /**
   * 校验找回密码短信验证码
   * @param params 手机号与验证码
   * @returns 校验结果
   */
  static async verifyResetPasswordCode(
    params: VerifyResetPasswordCodeParams
  ): Promise<BackendResponse<VerifyResetPasswordCodeResponse>> {
    return request.post<VerifyResetPasswordCodeResponse>('/auth/password/verify-code', params);
  }

  /**
   * 校验短信验证码
   * @param params 手机号、验证码与场景
   * @returns 校验结果
   */
  static async verifySmsCode(
    params: VerifySmsCodeParams
  ): Promise<BackendResponse<VerifySmsCodeResponse>> {
    return request.post<VerifySmsCodeResponse>('/auth/verify-code', params);
  }
}

export const {
  sendVerificationCode,
  register,
  checkPhoneRegistration,
  checkUsernameRegistration,
  login,
  userLogin,
  getLoginValidateImage,
  getUserInfo,
  logout,
  changeUserPassword,
  resetPassword,
  verifyResetPasswordCode,
  verifySmsCode,
} = AuthAPI;
